'use client';

import { Button, cn } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { CommonCarousel } from '../../Carousel';

type HomeFeaturesCarouselSlide = Readonly<{
  /** Keys into `<namespace>.slides.<id>`. */
  id: string;
  artworkSrc: string;
  ctaTrackName: string;
  artworkClassName?: string;
  href: string;
}>;

type HomeFeaturesCarouselProps = Readonly<{
  namespace: string;
  slides: ReadonlyArray<HomeFeaturesCarouselSlide>;
  headingId?: string;
  className?: string;
}>;

type HomeFeaturesCarouselPodProps = Readonly<{
  slide: HomeFeaturesCarouselSlide;
  title: string;
  ctaLabel: string;
  href: string;
}>;

function HomeFeaturesCarouselPod({ slide, title, ctaLabel, href }: HomeFeaturesCarouselPodProps) {
  const headingId = `home-features-carousel-pod-${slide.id}-heading`;

  return (
    <article
      aria-labelledby={headingId}
      data-testid={`home-features-carousel-pod-${slide.id}`}
      className="light relative flex aspect-[16/9] min-h-45 w-full flex-col justify-end overflow-hidden rounded-2xl select-none"
      data-track-container="Promotion"
      data-track-id={title}
    >
      <Image
        src={slide.artworkSrc}
        alt=""
        aria-hidden
        draggable={false}
        fill
        sizes="(max-width: 300px) 100vw, 300px"
        className={cn('pointer-events-none object-contain object-top', slide.artworkClassName ?? 'object-cover object-center')}
      />
      <div
        aria-hidden
        data-testid={`home-features-carousel-scrim-${slide.id}`}
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent from-[45.6%] to-black/60"
      />
      <h3 id={headingId} className="sr-only">
        {title}
      </h3>
      <Button asChild variant="secondary" size="m" className="relative m-4 justify-center" data-track-name={slide.ctaTrackName}>
        <Link href={href} data-testid={`home-features-carousel-cta-${slide.id}`}>
          {ctaLabel}
        </Link>
      </Button>
    </article>
  );
}

function HomeFeaturesCarousel({ namespace, slides, headingId = 'home-features-carousel-heading', className }: HomeFeaturesCarouselProps) {
  const t = useTranslations(namespace);

  if (slides.length === 0) return null;

  return (
    <section aria-labelledby={headingId} data-testid="home-features-carousel" className={cn('w-full', className)}>
      <h2 id={headingId} className="sr-only">
        {t('title')}
      </h2>
      <CommonCarousel
        shadcnOptions={{ align: 'start', containScroll: 'trimSnaps', slidesToScroll: 1 }}
        options={{
          fullWidth: true,
          showHeader: false,
          showControls: false,
          showDots: false,
          showCount: false,
          disableHoverEffect: true,
          basis: 'w-75 max-w-[calc(100dvw-var(--ll-container-padding)*2)]',
          itemGap: 'pl-3',
          contentMargin: '-ml-3',
          contentYPadding: '',
        }}
        data-track-carousel-title="Promotions"
      >
        {slides.map(slide => (
          <HomeFeaturesCarouselPod key={slide.id} slide={slide} title={t(`slides.${slide.id}.title`)} ctaLabel={t(`slides.${slide.id}.cta`)} href={slide.href} />
        ))}
      </CommonCarousel>
    </section>
  );
}

export { HomeFeaturesCarousel };
export type { HomeFeaturesCarouselProps, HomeFeaturesCarouselSlide };
